import { useReducer, useState, useCallback, useEffect } from "react"
import { AuthFlowStep, executeAuthFlowStep, getAuthFlowSteps } from "./gql"
import {
  stepStatesReducer,
  StepStatesMap,
  StepState,
  getStepState as getStepStateFromMap,
  serializeStepStates,
  deserializeStepStates,
} from "./step_state_reducer"

// Sets up the activation state for test callback before redirecting to OAuth
// This allows the authorize action to redirect back to the test page after OAuth
async function setupTestCallback(serviceId: string, callbackUrl: string): Promise<boolean> {
  try {
    const url = `/services/${serviceId}/api/setup_test_callback`
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
      },
      body: JSON.stringify({ callback_url: callbackUrl }),
    })
    if (!response.ok) {
      console.error("[ModularAuthSteps] Failed to setup test callback:", await response.text())
      return false
    }
    return true
  } catch (err) {
    console.error("[ModularAuthSteps] Error setting up test callback:", err)
    return false
  }
}

interface UseAuthFlowExecutionOptions {
  channelModuleName: string
  callbackParams?: Record<string, string> | null
}

interface UseAuthFlowExecutionResult {
  // Data
  steps: AuthFlowStep[]
  loading: boolean
  loadError: string | null
  context: Record<string, unknown>
  flowCompleted: boolean
  resetCount: number

  // Step state
  currentStepIndex: number | null
  isExecuting: boolean
  getStepState: (stepName: string) => StepState

  // Actions
  startTest: () => void
  reset: () => void
  executeStep: (stepName: string, options?: ExecuteStepOptions) => Promise<void>
  runStep: (stepName: string, stepIndex: number) => void
  retryStep: (stepName: string) => void
  submitForm: (stepName: string, values: Record<string, string>) => void

  // Context editing
  editingContext: boolean
  contextEditValue: string
  setContextEditValue: (value: string) => void
  startEditingContext: () => void
  saveContext: () => boolean
  cancelEditingContext: () => void
}

interface ExecuteStepOptions {
  userInput?: Record<string, string>
  callbackParams?: Record<string, string>
  currentContext?: Record<string, unknown>
  autoAdvance?: boolean
  // Track step states through recursive calls (for serialization before redirect)
  pendingStepStates?: Map<string, StepState>
}

// Compute which fields changed between before and after context
function computeContextChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!after) return null

  const changes: Record<string, unknown> = {}

  for (const key of Object.keys(after)) {
    const beforeValue = before[key]
    const afterValue = after[key]

    // Include if key is new or value changed
    if (!(key in before) || JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
      changes[key] = afterValue
    }
  }

  return Object.keys(changes).length > 0 ? changes : null
}

export function useAuthFlowExecution({
  channelModuleName,
  callbackParams,
}: UseAuthFlowExecutionOptions): UseAuthFlowExecutionResult {
  // Steps loaded from server
  const [steps, setSteps] = useState<AuthFlowStep[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Client-managed execution context
  const [context, setContext] = useState<Record<string, unknown>>({})
  const [editingContext, setEditingContext] = useState(false)
  const [contextEditValue, setContextEditValue] = useState("")

  // Execution state using reducer
  const [stepStates, dispatch] = useReducer(stepStatesReducer, new Map() as StepStatesMap)
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const [flowCompleted, setFlowCompleted] = useState(false)
  const [resetCount, setResetCount] = useState(0)

  // For resuming from OAuth callback
  const [pendingCallbackStepName, setPendingCallbackStepName] = useState<string | null>(null)

  // Load steps on mount
  useEffect(() => {
    getAuthFlowSteps(channelModuleName)
      .then(fetchedSteps => {
        const sortedSteps = [...fetchedSteps].sort((a, b) => a.position - b.position)
        setSteps(sortedSteps)
        setLoading(false)

        // Check if we're returning from OAuth callback
        if (callbackParams && Object.keys(callbackParams).length > 0) {
          const storedStepName = sessionStorage.getItem(`auth_flow_callback_step_${channelModuleName}`)
          const storedContext = sessionStorage.getItem(`auth_flow_context_${channelModuleName}`)
          const storedStepStates = sessionStorage.getItem(`auth_flow_step_states_${channelModuleName}`)
          if (storedStepName && storedContext) {
            try {
              setContext(JSON.parse(storedContext))
              setPendingCallbackStepName(storedStepName)
              const stepIndex = sortedSteps.findIndex(s => s.name === storedStepName)
              if (stepIndex >= 0) {
                setCurrentStepIndex(stepIndex)
              }
              if (storedStepStates) {
                dispatch({ type: "RESTORE_STATES", states: deserializeStepStates(storedStepStates) })
              }
            } catch (e) {
              console.error("Failed to restore callback state:", e)
            }
            sessionStorage.removeItem(`auth_flow_callback_step_${channelModuleName}`)
            sessionStorage.removeItem(`auth_flow_context_${channelModuleName}`)
            sessionStorage.removeItem(`auth_flow_step_states_${channelModuleName}`)
          }
        }
      })
      .catch(err => {
        setLoadError("Failed to load auth flow steps")
        setLoading(false)
        console.error("Error fetching auth flow steps:", err)
      })
  }, [channelModuleName, callbackParams])

  // Execute a step
  const executeStep = useCallback(
    async (stepName: string, options: ExecuteStepOptions = {}) => {
      setIsExecuting(true)
      dispatch({ type: "STEP_STARTED", stepName })

      const executionContext = options.currentContext ?? context
      // Track step states through recursive calls for serialization
      const pendingStates = options.pendingStepStates ?? new Map(stepStates)

      try {
        const result = await executeAuthFlowStep(channelModuleName, stepName, {
          context: executionContext,
          userInput: options.userInput,
          callbackParams: options.callbackParams,
        })

        // Check for redirect first - OAuth steps return success=true with a redirect_url
        if (result.redirect_url) {
          // Store state for OAuth callback resumption
          // Use pendingStates which includes all completed steps from this execution chain
          sessionStorage.setItem(`auth_flow_callback_step_${channelModuleName}`, stepName)
          sessionStorage.setItem(`auth_flow_context_${channelModuleName}`, JSON.stringify(executionContext))
          sessionStorage.setItem(`auth_flow_step_states_${channelModuleName}`, serializeStepStates(pendingStates))
          dispatch({ type: "STEP_NEEDS_REDIRECT", stepName, result })

          // Set up test callback and redirect to OAuth provider
          const callbackUrl = window.location.href.split("?")[0]
          const setupSuccess = await setupTestCallback(channelModuleName, callbackUrl)
          if (setupSuccess) {
            window.location.href = result.redirect_url
          } else {
            dispatch({
              type: "STEP_FAILED",
              stepName,
              error: "Failed to set up OAuth callback. Please try again.",
              result,
            })
          }
        } else if (result.success) {
          const newContext = result.output_context || executionContext
          const contextChanges = computeContextChanges(executionContext, result.output_context)
          setContext(newContext)
          dispatch({ type: "STEP_COMPLETED", stepName, result, contextChanges })

          // Update pendingStates with this completed step
          pendingStates.set(stepName, {
            status: "completed",
            result,
            contextChanges,
          })

          const stepIndex = steps.findIndex(s => s.name === stepName)
          if (stepIndex < steps.length - 1) {
            const nextStep = steps[stepIndex + 1]
            setCurrentStepIndex(stepIndex + 1)

            if (options.autoAdvance) {
              await new Promise(resolve => setTimeout(resolve, 100))

              if (nextStep.needs_user_input && nextStep.input_method === "FORM") {
                dispatch({ type: "STEP_NEEDS_INPUT", stepName: nextStep.name })
                return
              }

              return executeStep(nextStep.name, {
                currentContext: newContext,
                autoAdvance: !nextStep.needs_user_input,
                pendingStepStates: pendingStates,
              })
            }
          } else {
            setFlowCompleted(true)
            setCurrentStepIndex(null)
          }
        } else {
          const step = steps.find(s => s.name === stepName)
          const needsFormInput = step?.input_method === "FORM" && step?.needs_user_input

          if (needsFormInput && !options.userInput) {
            dispatch({ type: "STEP_NEEDS_INPUT", stepName, result })
          } else {
            dispatch({
              type: "STEP_FAILED",
              stepName,
              error: result.error_message || "Step execution failed",
              result,
            })
          }
        }
      } catch (err) {
        dispatch({
          type: "STEP_FAILED",
          stepName,
          error: err instanceof Error ? err.message : "Execution failed",
        })
      } finally {
        setIsExecuting(false)
      }
    },
    [context, steps, channelModuleName, stepStates]
  )

  // Handle OAuth callback resumption
  useEffect(() => {
    if (pendingCallbackStepName && callbackParams && !isExecuting) {
      executeStep(pendingCallbackStepName, { callbackParams, autoAdvance: true })
      setPendingCallbackStepName(null)
    }
  }, [pendingCallbackStepName, callbackParams, isExecuting, executeStep])

  // Public actions
  const startTest = useCallback(() => {
    setContext({})
    dispatch({ type: "RESET" })
    setCurrentStepIndex(0)
    setFlowCompleted(false)
    if (steps.length > 0) {
      executeStep(steps[0].name, { autoAdvance: true })
    }
  }, [steps, executeStep])

  const reset = useCallback(() => {
    setContext({})
    dispatch({ type: "RESET" })
    setCurrentStepIndex(null)
    setFlowCompleted(false)
    setEditingContext(false)
    setResetCount(prev => prev + 1)
  }, [])

  const runStep = useCallback(
    (stepName: string, stepIndex: number) => {
      setCurrentStepIndex(stepIndex)
      executeStep(stepName)
    },
    [executeStep]
  )

  const retryStep = useCallback(
    (stepName: string) => {
      executeStep(stepName)
    },
    [executeStep]
  )

  const submitForm = useCallback(
    (stepName: string, values: Record<string, string>) => {
      executeStep(stepName, { userInput: values, autoAdvance: true })
    },
    [executeStep]
  )

  // Context editing actions
  const startEditingContext = useCallback(() => {
    setContextEditValue(JSON.stringify(context, null, 2))
    setEditingContext(true)
  }, [context])

  const saveContext = useCallback((): boolean => {
    try {
      const parsed = JSON.parse(contextEditValue)
      setContext(parsed)
      setEditingContext(false)
      return true
    } catch {
      return false
    }
  }, [contextEditValue])

  const cancelEditingContext = useCallback(() => {
    setEditingContext(false)
  }, [])

  const getStepState = useCallback(
    (stepName: string) => getStepStateFromMap(stepStates, stepName),
    [stepStates]
  )

  return {
    steps,
    loading,
    loadError,
    context,
    flowCompleted,
    resetCount,
    currentStepIndex,
    isExecuting,
    getStepState,
    startTest,
    reset,
    executeStep,
    runStep,
    retryStep,
    submitForm,
    editingContext,
    contextEditValue,
    setContextEditValue,
    startEditingContext,
    saveContext,
    cancelEditingContext,
  }
}
